import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import QuotesheetGenerator from './QuotesheetGenerator';
import QuotesheetEditorModal from './QuotesheetEditorModal';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-accent/5 transition-colors text-left"
      >
        <span className="font-medium text-foreground text-sm">{title}</span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-text-secondary flex-shrink-0 ml-2" />
      </button>
      {isOpen && (
        <div className="px-4 py-4 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
};

const QuoteCustomization = ({ formData, updateFormData, errors }) => {
  const [generatedQuotesheet, setGeneratedQuotesheet] = useState(formData?.quoteSettings?.quotesheet || null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [autoLiteTriggered, setAutoLiteTriggered] = useState(false);

  // Auto-check default required information fields on first load
  useEffect(() => {
    const defaultChecked = ['pricing-breakdown', 'timeline', 'payment-terms', 'insurance'];
    const currentRequirements = formData?.quoteSettings?.requirements;
    if (!currentRequirements || currentRequirements?.length === 0) {
      updateFormData('quoteSettings', {
        ...formData?.quoteSettings,
        requirements: defaultChecked,
      });
    }
  }, []);

  // Auto-generate Quote Lite for service stacks on first load
  useEffect(() => {
    if (autoLiteTriggered) return;
    if (formData?.basics?.isOnlineOnly) return;
    if (generatedQuotesheet) return;
    setAutoLiteTriggered(true);
  }, []);

  const deadlineOptions = [
    { value: '', label: 'Select response deadline' },
    { value: '3-days', label: '3 days' },
    { value: '1-week', label: '1 week' },
    { value: '2-weeks', label: '2 weeks' },
    { value: '1-month', label: '1 month' },
    { value: 'custom', label: 'Custom date' }
  ];

  const handleInputChange = (field, value) => {
    updateFormData('quoteSettings', { ...formData?.quoteSettings, [field]: value });
  };

  const handleCheckboxChange = (field, checked) => {
    const currentRequirements = formData?.quoteSettings?.requirements || [];
    let updatedRequirements;

    if (checked) {
      updatedRequirements = [...currentRequirements, field];
    } else {
      updatedRequirements = currentRequirements?.filter(req => req !== field);
    }

    updateFormData('quoteSettings', { 
      ...formData?.quoteSettings, 
      requirements: updatedRequirements 
    });
  };

  const isRequirementChecked = (field) => {
    return (formData?.quoteSettings?.requirements || [])?.includes(field);
  };

  const handleQuotesheetGenerated = (quotesheet) => {
    setGeneratedQuotesheet(quotesheet);
    setIsEditorOpen(true);
  };

  const handleQuotesheetLiteGenerated = (quotesheet) => {
    setGeneratedQuotesheet(quotesheet);
    updateFormData('quoteSettings', {
      ...formData?.quoteSettings,
      quotesheet,
      quotesheetActive: true
    });
  };

  const handleUseQuotesheet = (quotesheet) => {
    updateFormData('quoteSettings', { 
      ...formData?.quoteSettings, 
      quotesheet,
      quotesheetActive: true
    });
    setGeneratedQuotesheet(quotesheet);
    setIsEditorOpen(false);
  };

  const handleSaveQuotesheet = (quotesheet) => {
    setGeneratedQuotesheet(quotesheet);
    updateFormData('quoteSettings', { 
      ...formData?.quoteSettings, 
      quotesheet
    });
  };

  const handleEditQuotesheet = () => {
    setIsEditorOpen(true);
  };

  const handleRemoveQuotesheet = () => {
    setGeneratedQuotesheet(null);
    updateFormData('quoteSettings', { 
      ...formData?.quoteSettings, 
      quotesheet: null,
      quotesheetActive: false
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Quote Request Settings</h3>
        <p className="text-text-secondary mb-6">Customize what information vendors should provide and set response deadlines.</p>
      </div>

      {/* Quotesheet Generator Section */}
      {!formData?.basics?.isOnlineOnly && (
        <div className="space-y-4">
          {!generatedQuotesheet ? (
            <QuotesheetGenerator 
              formData={formData} 
              onGenerate={handleQuotesheetGenerated}
              onGenerateLite={handleQuotesheetLiteGenerated}
              autoGenerateLite={autoLiteTriggered}
              onAutoLiteComplete={() => setAutoLiteTriggered(false)}
            />
          ) : (
            <div className="bg-success/5 border border-success/20 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <Icon name="CheckCircle" size={20} className="mr-2 text-success" />
                    <h4 className="font-semibold text-foreground">Quotesheet Template Ready</h4>
                    {generatedQuotesheet?.type === 'lite' && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent font-medium">Lite</span>
                    )}
                    {formData?.quoteSettings?.quotesheetActive && (
                      <span className="ml-3 px-2 py-1 text-xs rounded-full bg-success text-white">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary mb-3">
                    Your {generatedQuotesheet?.type === 'lite' ? 'Quote Lite' : 'custom'} quotesheet has been generated with {generatedQuotesheet?.sections?.length} sections and {generatedQuotesheet?.sections?.reduce((total, s) => total + s?.fields?.length, 0)} fields.
                    {formData?.quoteSettings?.quotesheetActive 
                      ? ' This template will be shared with vendors when the project is created.'
                      : ' Click "Use Quotesheet" to activate it for this project.'}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditQuotesheet}
                      iconName="Edit"
                      iconPosition="left"
                    >
                      Edit Quotesheet
                    </Button>
                    {!formData?.quoteSettings?.quotesheetActive && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleUseQuotesheet(generatedQuotesheet)}
                        iconName="CheckCircle"
                        iconPosition="left"
                      >
                        Use Quotesheet
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveQuotesheet}
                      iconName="Trash2"
                      iconPosition="left"
                      className="text-error hover:bg-error/10"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {/* Response Deadline */}
        <CollapsibleSection title="Response Deadline">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Response Deadline"
              description="Vendors must submit quotes before this deadline"
              options={deadlineOptions}
              value={formData?.quoteSettings?.deadline || ''}
              onChange={(value) => handleInputChange('deadline', value)}
              error={errors?.deadline}
              required
            />
            {formData?.quoteSettings?.deadline === 'custom' && (
              <Input
                label="Custom Deadline Date"
                type="date"
                description="Select the exact date vendors must respond by"
                value={formData?.quoteSettings?.customDeadline || ''}
                onChange={(e) => handleInputChange('customDeadline', e?.target?.value)}
                min={new Date()?.toISOString()?.split('T')?.[0]}
                required
              />
            )}
          </div>
        </CollapsibleSection>

        {/* Required Information */}
        <CollapsibleSection title="Required Information from Vendors">
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">Select what information vendors must provide in their quotes. Checking more items leads to more detailed and comparable quotes.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Checkbox
                label="Detailed pricing breakdown"
                checked={isRequirementChecked('pricing-breakdown')}
                onChange={(e) => handleCheckboxChange('pricing-breakdown', e?.target?.checked)}
              />
              <Checkbox
                label="Delivery/completion timeline"
                checked={isRequirementChecked('timeline')}
                onChange={(e) => handleCheckboxChange('timeline', e?.target?.checked)}
              />
              <Checkbox
                label="Warranty information"
                checked={isRequirementChecked('warranty')}
                onChange={(e) => handleCheckboxChange('warranty', e?.target?.checked)}
              />
              <Checkbox
                label="Payment terms and options"
                checked={isRequirementChecked('payment-terms')}
                onChange={(e) => handleCheckboxChange('payment-terms', e?.target?.checked)}
              />
              <Checkbox
                label="Product/service specifications"
                checked={isRequirementChecked('specifications')}
                onChange={(e) => handleCheckboxChange('specifications', e?.target?.checked)}
              />
              <Checkbox
                label="References or portfolio"
                checked={isRequirementChecked('references')}
                onChange={(e) => handleCheckboxChange('references', e?.target?.checked)}
              />
              <Checkbox
                label="Insurance and licensing info"
                checked={isRequirementChecked('insurance')}
                onChange={(e) => handleCheckboxChange('insurance', e?.target?.checked)}
              />
              <Checkbox
                label="Additional fees or charges"
                checked={isRequirementChecked('additional-fees')}
                onChange={(e) => handleCheckboxChange('additional-fees', e?.target?.checked)}
              />
            </div>
          </div>
        </CollapsibleSection>

        {/* Special Instructions */}
        <CollapsibleSection title="Special Instructions">
          <Input
            label="Special Instructions"
            type="text"
            placeholder="Any specific instructions or questions for vendors..."
            description="Optional: Provide additional context or specific questions for vendors to answer"
            value={formData?.quoteSettings?.instructions || ''}
            onChange={(e) => handleInputChange('instructions', e?.target?.value)}
          />
        </CollapsibleSection>

        {/* Vendor Permitted Contact */}
        <CollapsibleSection title="Vendor Permitted Contact">
          <div className="space-y-3">
            <p className="text-sm text-text-secondary mb-2">How would you like to receive quotes?</p>
            <Checkbox
              label="Allow phone consultations"
              description="Vendors can call to discuss details before submitting quotes"
              checked={formData?.quoteSettings?.allowPhoneCalls || false}
              onChange={(e) => handleInputChange('allowPhoneCalls', e?.target?.checked)}
            />
            <Checkbox
              label="Request site visits (if applicable)"
              description="For purchases that may require in-person assessment"
              checked={formData?.quoteSettings?.allowSiteVisits || false}
              onChange={(e) => handleInputChange('allowSiteVisits', e?.target?.checked)}
            />
            <Checkbox
              label="Accept alternative suggestions"
              description="Allow vendors to suggest alternative products/services"
              checked={formData?.quoteSettings?.allowAlternatives || false}
              onChange={(e) => handleInputChange('allowAlternatives', e?.target?.checked)}
            />
          </div>
        </CollapsibleSection>

        {/* Contact Preferences */}
        <CollapsibleSection title="Contact Preferences">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Preferred Contact Method"
              type="text"
              placeholder="e.g., Email preferred, phone OK"
              value={formData?.quoteSettings?.contactMethod || ''}
              onChange={(e) => handleInputChange('contactMethod', e?.target?.value)}
            />
            <Input
              label="Best Time to Contact"
              type="text"
              placeholder="e.g., Weekdays 9-5 EST"
              value={formData?.quoteSettings?.contactTime || ''}
              onChange={(e) => handleInputChange('contactTime', e?.target?.value)}
            />
          </div>
        </CollapsibleSection>
      </div>

      {/* Quotesheet Editor Modal */}
      <QuotesheetEditorModal
        isOpen={isEditorOpen}
        quotesheet={generatedQuotesheet}
        onClose={() => setIsEditorOpen(false)}
        onUse={handleUseQuotesheet}
        onSave={handleSaveQuotesheet}
      />
    </div>
  );
};

export default QuoteCustomization;